import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Flex,
  Popconfirm,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FC, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import {
  type IncorporationCandidate,
  type IncorporationCompany,
  type IncorporationParticipant,
  type MdsFillResult,
  type OwnerSelectionReason,
  type ProvisionResult,
  type ProvisionedMember,
  type RefreshFromMdsResult,
  useIncorporationDetails,
  useProvisionIncorporation,
  useRefreshFromMds,
  useSyncMdsStatus,
} from '@hooks/api';
import { captureMessage } from '@sentry/react';
import { getApiErrorMessage } from '@utils';

import AddressSection from './AddressSection';
import ContactPersonSection from './ContactPersonSection';
import { formatDate } from './columns';
import * as S from './styled';

const { Text } = Typography;

/** Why this person, and not one of the others, gets the company. Shown so the choice is auditable. */
const OWNER_REASON_LABEL: Record<OwnerSelectionReason, string> = {
  mds_sso_owner: 'Named by Master Data as the portal account holder',
  managing_director: 'The only managing director among the linked people',
  earliest_linked_person:
    'No indicator in Master Data — the earliest-created linked person',
};

const LINK_KIND_LABEL: Record<string, string> = {
  managing_director: 'Managing director',
  director: 'Director',
  shareholder: 'Shareholder',
  ubo: 'UBO',
  role: 'Role',
};

type Props = {
  company: IncorporationCompany | null;
  onClose: () => void;
};

/**
 * Everyone provisioning writes into legacy — a superset of the candidate table above it.
 *
 * The two are shown separately rather than merged, because they answer different questions and a
 * merged table would answer neither cleanly: the candidate table is about *who gets a login*, and
 * carries the owner choice and the membership state; this one is about *who ends up in the
 * company*. Before it existed, a CSM looking at a company with one portal user and three
 * participants saw one row and reasonably concluded the other two had been lost.
 */
const participantColumns = [
  {
    title: 'Participant',
    key: 'name',
    render: (_: unknown, participant: IncorporationParticipant) => (
      <Flex gap={8} align="center" wrap>
        <span>{participant.name}</span>
        {participant.type === 'legal' && (
          <Tooltip title="A shareholding company. Legacy stores it as a person row of type `legal`; it cannot hold a portal account.">
            <Tag>Company</Tag>
          </Tooltip>
        )}
      </Flex>
    ),
  },
  {
    title: 'Linked as',
    key: 'kinds',
    render: (_: unknown, participant: IncorporationParticipant) => (
      <Flex gap={4} wrap>
        {participant.kinds.map((kind) => (
          <Tag key={kind}>{LINK_KIND_LABEL[kind] ?? kind}</Tag>
        ))}
      </Flex>
    ),
  },
  {
    title: 'Shares',
    key: 'sharesAmount',
    width: 90,
    // An em dash rather than 0: Master Data recording no share count is not the same as holding no
    // shares, and printing a zero would state the second.
    render: (_: unknown, participant: IncorporationParticipant) =>
      participant.sharesAmount ?? '—',
  },
  {
    title: 'Portal account',
    key: 'canHoldPortalAccount',
    width: 140,
    /*
     * Said for both answers, not only the negative one. "No" beside a person is a fact about Master
     * Data a CSM can act on — add a `linked_user_id` — while a blank cell reads as an oversight in
     * this page.
     */
    render: (_: unknown, participant: IncorporationParticipant) =>
      participant.canHoldPortalAccount ? (
        <Tag color="success">Yes</Tag>
      ) : (
        <Tooltip title="No Keycloak identity in Master Data, so there is nothing to attach a portal account or the easybiz-client role to. They are still written into legacy.">
          <Tag>No</Tag>
        </Tooltip>
      ),
  },
];

const candidateColumns = [
  {
    title: 'Person',
    key: 'name',
    render: (_: unknown, candidate: IncorporationCandidate) => (
      <Flex gap={8} align="center" wrap>
        <span>
          {candidate.firstName} {candidate.lastName}
        </span>
        {candidate.isOwner && <Tag color="blue">Owner</Tag>}
        {/*
         * Shown only when the backend says so, never derived from its absence: a backend deployed
         * before the field sends nothing, and rendering "will be attached" for that would claim the
         * opposite of what may be true. The absence of the tag says nothing either way, which is
         * what the page said about everybody before this.
         */}
        {candidate.alreadyMember === true && (
          <Tooltip title="Legacy already holds this person as a member of the matched company, so no membership is created for them. A rerun still (re-)grants their easybiz-client role.">
            <Tag>Already a member</Tag>
          </Tooltip>
        )}
      </Flex>
    ),
  },
  {
    title: 'Linked as',
    key: 'kinds',
    render: (_: unknown, candidate: IncorporationCandidate) => (
      <Flex gap={4} wrap>
        {candidate.kinds.map((kind) => (
          <Tag key={kind}>{LINK_KIND_LABEL[kind] ?? kind}</Tag>
        ))}
        {candidate.roleTypes.map((roleType) => (
          <Tag key={roleType}>{roleType}</Tag>
        ))}
      </Flex>
    ),
  },
  {
    title: 'In MDS since',
    key: 'createdAt',
    width: 130,
    render: (_: unknown, candidate: IncorporationCandidate) =>
      formatDate(candidate.createdAt),
  },
];

/**
 * `onClick` handlers must not hand a rejected promise back to antd: the error is already rendered
 * from the mutation's own `isError` below, but an uncaught rejection still reaches the console and
 * Sentry. Module scope because it closes over nothing.
 */
const swallow = (run: () => Promise<void>) => () => {
  void run().catch(() => undefined);
};

/**
 * One company from the queue, and the button that brings it into the platform.
 *
 * The people are shown before the action rather than after it, with the owner already marked and the
 * reason spelled out: provisioning creates a tenant schema and hands out portal accounts, and none of
 * that is undone by closing the drawer. The CSM confirms a decision the backend has already made and
 * explained, rather than making one from a list.
 *
 * The contents are keyed on the company, so React remounts them whenever a different row is opened.
 * That is what keeps one company's outcome out of another's drawer, and it replaces three hand-rolled
 * mechanisms that were doing the same job worse: an effect clearing every state value on a prop
 * change, a `reset()` on each mutation, and a ref written during render so async callbacks could tell
 * whether the drawer had moved on. A remount resets all of it, and a late response from the previous
 * company now lands on an unmounted component, where React ignores it.
 */
const IncorporationDrawer: FC<Props> = ({ company, onClose }) => (
  <Drawer
    open={Boolean(company)}
    onClose={onClose}
    width={720}
    title={company?.legalName ?? ''}
    destroyOnClose
  >
    {company && (
      <DrawerContents
        key={company.mdsCompanyId}
        company={company}
        onClose={onClose}
      />
    )}
  </Drawer>
);

/** Everything inside the drawer, remounted per company by the `key` above. */
const DrawerContents: FC<{
  company: IncorporationCompany;
  onClose: () => void;
}> = ({ company, onClose }) => {
  const mdsCompanyId = company.mdsCompanyId;
  const { data, isPending, error, refetch, isFetching } =
    useIncorporationDetails(mdsCompanyId);
  const provision = useProvisionIncorporation();
  const syncStatus = useSyncMdsStatus();
  const refresh = useRefreshFromMds();
  const [incorporationDate, setIncorporationDate] = useState<Dayjs | null>(
    null
  );
  const [result, setResult] = useState<ProvisionResult | null>(null);
  /** The status MDS reported when it resolved the retry without actually applying it. */
  const [syncRefused, setSyncRefused] = useState<string | null>(null);
  /** What the standalone "Refresh from Master Data" run moved, once it has been pressed. */
  const [refreshed, setRefreshed] = useState<RefreshFromMdsResult | null>(null);
  /**
   * That this drawer provisioned this company — the eligibility `result` used to stand for.
   *
   * Kept apart from `result` because the two have different lifetimes: `result` is one run's output
   * and has to be cleared when another run replaces it, while "this page provisioned this company"
   * is true for the rest of the mount however many times the panels turn over. Folded together, the
   * clear below would take the Refresh button away with the panel.
   */
  const [provisioned, setProvisioned] = useState(false);
  /** The CSM's say over the contact person: on by default, switched off in its section. */
  const [overwriteContact, setOverwriteContact] = useState(true);
  /** The same, for the address. */
  const [overwriteAddress, setOverwriteAddress] = useState(true);

  const needsDate = Boolean(data && !data.company.incorporationDate);
  const isExistingClient = data?.company.legacyMatchedBy === 'reference_num';
  /**
   * An existing client every linked person is already a member of.
   *
   * `=== true` per candidate, so a backend that does not report membership yet leaves this false and
   * the wording unchanged — the honest default, since "nobody's access changes" is a claim and its
   * absence is not. `candidates.length > 0` because a company with nobody to attach is a different
   * state entirely: there the action is disabled and the warnings say why.
   */
  const nobodyLeftToAttach = Boolean(
    isExistingClient &&
      data &&
      data.candidates.length > 0 &&
      data.candidates.every((candidate) => candidate.alreadyMember === true)
  );
  /**
   * Whether the section heading's blanket claim is false for at least one row.
   *
   * `some`, not `nobodyLeftToAttach`: "People who will get a portal account" is wrong the moment one
   * person already has one, so the mixed case needs the neutral heading as much as the all-members
   * one does. Read off the tags rather than off `isExistingClient`/`isRerun`, so the heading and the
   * per-person tags can never disagree — and so an unreported field (nothing tagged) leaves the
   * heading alone, which is the same "absence states nothing" rule the tag itself follows.
   */
  const someAlreadyMember = Boolean(
    data?.candidates.some((candidate) => candidate.alreadyMember === true)
  );
  /**
   * The status-only retry is for one state and one only: a company *this page* provisioned whose MDS
   * half did not land. A `reference_num` match also carries a `legacyCompanyId`, so gating on that
   * would offer the button for an existing client — where it would move MDS to in_service, drop the
   * row out of a queue that lists only `incorporating`, and leave nobody with a portal account. The
   * backend refuses that too; this is the half that stops it being offered.
   */
  const isOurDesync = data?.company.legacyMatchedBy === 'idempotency_key';
  /**
   * Whether pressing the primary button again would *finish* a provisioning rather than start one.
   *
   * `isOurDesync` alone is server state, and this mount is where it goes stale: a run that leaves a
   * role ungranted is a first-time provisioning, so `legacyMatchedBy` is still null until the
   * details query refetches — and this read is VPN-only across five MDS endpoints, so a failed
   * background refetch leaves the old value standing indefinitely (see the alert above).
   * `provisioned` is the local fact that cannot be stale: this drawer provisioned this company. It
   * is a flag of its own rather than `result !== null` because the result panels are cleared when
   * another action replaces them, and eligibility must outlive that.
   */
  const isRerun = isOurDesync || provisioned;
  /**
   * Refresh is wider than a rerun: the backend also admits a client legacy holds under the MDS id —
   * one this page never provisioned, typically because compliance has not approved it — for a
   * data-only refresh. Kept apart from `isRerun`, which also renames the primary button to "Finish"
   * and must not for that kind of company.
   *
   * The backend's answer wins over `isOurDesync` when it gives one: both are server state, and an
   * explicit `false` — a provisioned company whose legacy row is gone — is a refresh the route would
   * refuse. Absent (an older backend) falls back to `isOurDesync`. `provisioned` is the one input
   * that outranks it, for the reason `isRerun` keeps it: it is the local fact that cannot be stale,
   * while `data` is whatever the last successful read said, from before this drawer provisioned.
   */
  const canRefresh = provisioned || (data?.canRefresh ?? isOurDesync);
  const date = incorporationDate?.format('YYYY-MM-DD');

  const runProvision = async () => {
    // Both actions live in one mount, so nothing else clears this: the per-company `key` only resets
    // when the CSM opens a different row. Left standing, a refusal from an earlier "Retry MDS status"
    // renders directly above the success panel of the run that fixed it.
    setSyncRefused(null);
    // Same reason, other direction: a fill panel from an earlier standalone refresh would otherwise
    // sit under the fresh provisioning outcome, describing a different run.
    setRefreshed(null);

    const outcome = await provision.mutateAsync({
      mdsCompanyId,
      incorporationDate: date,
      updateContactPerson: overwriteContact,
      updateAddress: overwriteAddress,
    });

    // A partial success *resolves*, so `onError` never sees it — yet legacy now holds a company
    // whose MDS half is still incorporating, and the only record of that is a panel the CSM can
    // close. Reported so the drift outlives the drawer.
    if (!outcome.mdsUpdated) {
      captureMessage(
        'Incorporation provisioned but Master Data was not updated',
        {
          level: 'warning',
          tags: { feature: 'incorporations' },
          extra: {
            mdsCompanyId,
            mdsError: outcome.mdsError,
            schemaName: outcome.company.schemaName,
          },
        }
      );
    }

    setResult(outcome);
    setProvisioned(true);
  };

  const runRefresh = async () => {
    // Every other panel goes first, for the reason `runProvision` clears `syncRefused`: all three
    // actions live in one mount and nothing else resets them. Left standing, the provisioning
    // outcome and its own fill render above this run's result — three panels, two describing a run
    // that is over, and the older warnings read as belonging to the newest one.
    setRefreshed(null);
    setResult(null);
    setSyncRefused(null);

    // The whole result, not only its fill: the run grants portal accounts and the `easybiz-client`
    // role to anyone Master Data linked after provisioning, and a refusal there has nowhere else to
    // be read. This is the action a CSM presses *after* the company has left the queue, so a pod
    // log is the only other place it would exist.
    setRefreshed(
      await refresh.mutateAsync({
        mdsCompanyId,
        updateContactPerson: overwriteContact,
        updateAddress: overwriteAddress,
      })
    );
  };

  const runSync = async () => {
    setSyncRefused(null);

    const outcome = await syncStatus.mutateAsync({
      mdsCompanyId,
      incorporationDate: date,
    });

    // `mdsUpdated: false` is a resolved response, not a rejection: `syncStatus.isError` stays false
    // and no alert would render. Closing here would report the desync as fixed while the row is
    // still incorporating — the one thing this button exists to change.
    if (!outcome.mdsUpdated) {
      setSyncRefused(outcome.incorporationStatus);
      captureMessage('Master Data refused the incorporation status change', {
        level: 'warning',
        tags: { feature: 'incorporations' },
        extra: {
          mdsCompanyId,
          incorporationStatus: outcome.incorporationStatus,
        },
      });

      return;
    }

    // The company is now in service in MDS, so it leaves the queue; the outcome panel would be
    // describing a row that no longer exists.
    setResult(null);
    onClose();
  };

  // MDS is VPN-only and this read fans out across five of its endpoints, so a failure here is often
  // transient. Without a retry the only way back in was to close the drawer and click the row again.
  const readError = error ? (
    <Flex vertical gap={8} align="flex-start">
      <span>{getApiErrorMessage(error, 'Master Data did not answer.')}</span>
      <Button
        size="small"
        icon={<ReloadOutlined />}
        onClick={() => refetch()}
        loading={isFetching}
      >
        Try again
      </Button>
    </Flex>
  ) : null;

  return (
    <>
      {isPending ? (
        <Spin />
      ) : data ? (
        <Flex vertical gap={16}>
          {/* `data` is tested before `error` on purpose. react-query keeps `data` and sets `error`
              together when a *background* refetch fails, and `useProvisionIncorporation`
              invalidates this very query on success — so the other order replaced the provisioning
              outcome (tenant name, BOB reference, the MDS retry) with "could not read this company"
              moments after a tenant had been created, reading as though nothing had happened. */}
          {readError && (
            <Alert
              type="warning"
              showIcon
              message="Showing the last successful read — refreshing this company failed"
              description={readError}
            />
          )}
          {data.warnings.map((warning) => (
            <Alert key={warning} type="warning" showIcon message={warning} />
          ))}

          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Legal name">
              {data.company.legalName}
            </Descriptions.Item>
            <Descriptions.Item label="Legal form">
              {data.company.legalForm || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="RCS">
              {data.company.rcsNumber || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="VAT">
              {data.company.vatNumber || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="MDS status">
              {data.company.incorporationStatus}
            </Descriptions.Item>
            <Descriptions.Item label="Incorporated">
              {formatDate(data.company.incorporationDate)}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <S.SectionLabel strong>
              {someAlreadyMember
                ? 'Linked people'
                : 'People who will get a portal account'}
            </S.SectionLabel>
            {data.ownerSelectionReason && (
              <S.SectionNote type="secondary">
                Owner: {OWNER_REASON_LABEL[data.ownerSelectionReason]}.
              </S.SectionNote>
            )}
            <Table
              size="small"
              rowKey="personId"
              pagination={false}
              dataSource={data.candidates}
              columns={candidateColumns}
              locale={{
                emptyText: (
                  <S.BlockingEmptyText>
                    Nobody linked to this company in Master Data has a Keycloak
                    account, so there is no owner to provision.
                  </S.BlockingEmptyText>
                ),
              }}
            />
          </div>

          {/*
           * Rendered whenever the backend reports the field, empty list included — the "absence
           * states nothing" rule the tags above follow. A backend deployed before the field omits
           * it and the section stays away; a deployed backend that read nobody says so, because an
           * empty list is a fact about the company and hiding it would render the exact screen this
           * section exists to correct.
           */}
          {data.participants && (
            <div>
              <S.SectionLabel>Everyone written into legacy</S.SectionLabel>
              <S.SectionNote type="secondary">
                Provisioning writes all of these. The table above is the subset
                that can hold a portal account.
              </S.SectionNote>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={data.participants}
                columns={participantColumns}
                locale={{
                  emptyText: (
                    <S.BlockingEmptyText>
                      Master Data reports no participants for this company.
                    </S.BlockingEmptyText>
                  ),
                }}
              />
            </div>
          )}

          {data.contactPerson && (
            <ContactPersonSection
              plan={data.contactPerson}
              ownerNote={
                data.contactPerson.ownerSelectionReason
                  ? OWNER_REASON_LABEL[data.contactPerson.ownerSelectionReason]
                  : null
              }
              overwrite={overwriteContact}
              onOverwriteChange={setOverwriteContact}
            />
          )}

          {data.address && (
            <AddressSection
              plan={data.address}
              overwrite={overwriteAddress}
              onOverwriteChange={setOverwriteAddress}
            />
          )}

          {needsDate && (
            <div>
              <S.SectionLabel>
                Incorporation date <Text type="secondary">(optional)</Text>
              </S.SectionLabel>
              <DatePicker
                value={incorporationDate}
                onChange={setIncorporationDate}
                // Master Data requires this for an in-service company but its API does not enforce
                // it, so a record saved without one cannot be edited in the MDS admin afterwards.
                disabledDate={(current) =>
                  current && current > dayjs().endOf('day')
                }
              />
            </div>
          )}

          {result && (
            <ProvisionOutcome
              result={result}
              onRetry={swallow(runSync)}
              retrying={syncStatus.isPending}
            />
          )}

          {/*
           * The provisioning run fills legacy too, and its `filled.warnings` carry the one sentence
           * the majority population needs: a company legacy already had is provisioned with its
           * people and *without* its identity, so the RCS number the CSM came for was not carried.
           * Rendered here rather than inside `ProvisionOutcome` because it is the same panel the
           * standalone action produces, and one shape for one fact beats two.
           */}
          {result?.filled && <MdsFillOutcome fill={result.filled} />}

          {/* The standalone run, pressed days later for the RCS number and extract. */}
          {refreshed && <MdsFillOutcome fill={refreshed.filled ?? {}} />}

          {refreshed && (
            <RefreshRoleOutcome members={refreshed.members ?? []} />
          )}

          {refresh.isError && (
            <Alert
              type="error"
              showIcon
              message="Could not refresh from Master Data"
              description={getApiErrorMessage(
                refresh.error,
                'The request did not go through.'
              )}
            />
          )}

          {provision.isError && (
            <Alert
              type="error"
              showIcon
              message="Provisioning failed"
              description={getApiErrorMessage(
                provision.error,
                'The request did not go through.'
              )}
            />
          )}

          {syncStatus.isError && (
            <Alert
              type="error"
              showIcon
              message="Master Data was not updated"
              description={getApiErrorMessage(
                syncStatus.error,
                'The request did not go through.'
              )}
            />
          )}

          {/* The request went through; Master Data declined to move. Without this the drawer would
              just close, which reads as "fixed" for a row that is still incorporating. */}
          {syncRefused && (
            <Alert
              type="error"
              showIcon
              message="Master Data did not change the status"
              description={`The request went through, but Master Data still reports ${syncRefused}.`}
            />
          )}

          <Flex gap={8}>
            <Popconfirm
              /*
               * Three cases, not two, and the third is the one the role warning routes people into:
               * pressing this again on a company we already provisioned finishes what an earlier run
               * left, so "creates the company, its tenant schema and … cannot be undone" would flatly
               * contradict the panel above it. The button label already branched this way.
               */
              /*
               * `isRerun` FIRST, and the order is the finding rather than a style choice: on a
               * rerun the run's whole effect can be granting the easybiz-client role to people who
               * are already members (the backend grants it for `already_member` too), so
               * "no access changes hands" would contradict the panel above that just sent the CSM
               * here to fix exactly that.
               */
              title={
                isRerun
                  ? 'Finish provisioning this company?'
                  : nobodyLeftToAttach
                    ? 'Mark this client in service?'
                    : isExistingClient
                      ? 'Attach the linked people to the existing client?'
                      : 'Provision this company?'
              }
              description={
                isRerun
                  ? 'This company was already provisioned from here. Nothing is created twice — the run finishes whatever an earlier attempt left: memberships, the easybiz-client role, and the Master Data status.'
                  : nobodyLeftToAttach
                    ? "Everyone linked is already a member, so no membership is created. Master Data moves to in service, this MDS company is recorded against the legacy one, the tenant's migrations are brought up to date, and everyone linked is (re-)granted the easybiz-client role."
                    : isExistingClient
                      ? 'This client already exists in legacy. No company or tenant will be created — the linked people who are not members yet get portal accounts on it, and Master Data moves to in service.'
                      : 'Creates the company, its tenant schema and a portal account per linked person. This cannot be undone.'
              }
              okText={
                isRerun
                  ? 'Finish'
                  : nobodyLeftToAttach
                    ? 'Mark in service'
                    : isExistingClient
                      ? 'Attach'
                      : 'Provision'
              }
              onConfirm={swallow(runProvision)}
              disabled={!data.canProvision || provision.isPending}
            >
              <Button
                type="primary"
                loading={provision.isPending}
                disabled={!data.canProvision}
              >
                {/* The label is what a CSM reads before pressing, and for a mirrored live client
                    "Attach people" was usually describing work that would not happen. */}
                {isRerun
                  ? 'Finish provisioning'
                  : nobodyLeftToAttach
                    ? 'Mark in service'
                    : isExistingClient
                      ? 'Attach people and mark in service'
                      : 'Provision in legacy'}
              </Button>
            </Popconfirm>

            {/* `ProvisionOutcome` carries its own retry, and a partial success flips
                `legacyMatchedBy` to `idempotency_key` on the next read — so without the `!result`
                guard both render, with the same accessible name and the same handler. */}
            {isOurDesync && !result && (
              <Button onClick={swallow(runSync)} loading={syncStatus.isPending}>
                Retry MDS status
              </Button>
            )}

            {/*
             * Offered for a company this page provisioned (`isRerun`: `isOurDesync` is what the
             * server says, `result` the local fact that cannot be stale), and for one the backend
             * says it can refresh by id (`canRefresh`) — the backend's own guard is the same pair.
             *
             * It exists because the facts the Legal Docs Generator prints arrive after the button.
             * A company is provisioned the day it is incorporated; its RCS number and RCS extract
             * reach Master Data days later, and nothing else in this screen would ever carry them
             * into legacy.
             */}
            {/*
             * The identity fields, the address and the contact person are refreshed for every
             * company this button appears on, a client legacy already had included. The RCS extract
             * is not: it rides on the company's legal-entity row, which the backend writes only for
             * a company created from here. What actually happened is
             * reported afterwards, per run.
             */}
            {canRefresh && (
              <Tooltip title="Re-reads this company from Master Data: its name, VAT, RCS and tax numbers, its registered office as the address, the owner as its contact person, and people added since — plus, for a company created from here, its RCS extract. What Master Data does not hold is left as it is, except the parts of an address it replaces.">
                <Button
                  onClick={swallow(runRefresh)}
                  loading={refresh.isPending}
                >
                  Refresh from Master Data
                </Button>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ) : readError ? (
        <Alert
          type="error"
          showIcon
          message="Could not read this company from Master Data"
          description={readError}
        />
      ) : null}
    </>
  );
};

/**
 * What a fill from Master Data actually moved.
 *
 * Every count is read as `?? 0` and every list as `?? []`: the shape is hand-rolled over an
 * unvalidated response, so a backend deployed before one of these fields sends nothing — and
 * `undefined` rendered raw would put "undefined people added" in front of a CSM.
 *
 * The empty run gets its own wording rather than a list of zeros. "Nothing changed" is a real and
 * common outcome — pressing it twice, or before Master Data has the extract — and a panel of noughts
 * reads as a failure.
 */
/** Members that hold an account but whom Keycloak does not know as clients yet. */
const membersWithoutRole = (
  members: ProvisionedMember[]
): ProvisionedMember[] =>
  members.filter(
    (member) =>
      member.outcome !== 'failed' && member.clientRoleGranted === false
  );

/**
 * Who did not get the role, and what Keycloak said about each.
 *
 * The reason is what says which of the three fixes this is — create the realm role, or give the
 * service-account client the privilege to read it or to map it — and it is rendered verbatim rather
 * than translated: the sentence a CSM pastes to whoever administers the realm has to be the one
 * Keycloak said. A backend that does not report it yet simply names the person.
 */
const RoleFailureList: FC<{ members: ProvisionedMember[] }> = ({ members }) => (
  <S.ReasonList>
    {members.map((member) => (
      <li key={member.personId}>
        {member.email ?? member.keycloakId}
        {member.clientRoleError ? <>: {member.clientRoleError}</> : null}
      </li>
    ))}
  </S.ReasonList>
);

/**
 * The same refusal, after the standalone refresh.
 *
 * A refresh grants a newly-linked director their account and the `easybiz-client` role, so it can
 * fail the same way provisioning can — and it is the action pressed *after* the company has left a
 * queue that lists only `incorporating`, which makes this panel the only place the failure is read
 * at all. Pressing Refresh again re-grants: the role is applied to an existing member too.
 */
const RefreshRoleOutcome: FC<{ members: ProvisionedMember[] }> = ({
  members,
}) => {
  const withoutRole = membersWithoutRole(members);
  // Out of the people who got an account, not out of everyone the run touched: a member whose
  // account failed never reached the role at all, and counting them here makes the run read as
  // better than it was — "1 of 2" where one of the two was never in the running.
  const withAccount = members.filter((member) => member.outcome !== 'failed');

  if (withoutRole.length === 0) return null;

  return (
    <Alert
      type="warning"
      showIcon
      message={`${withoutRole.length} of ${withAccount.length} people did not get the easybiz-client role`}
      description={
        <Flex vertical gap={8}>
          <span>
            They are members of the company, but Keycloak has not been told they
            are clients of the platform. Press{' '}
            <strong>Refresh from Master Data</strong> again once the reason
            below is fixed — nothing is created twice.
          </span>
          <RoleFailureList members={withoutRole} />
        </Flex>
      }
    />
  );
};

const MdsFillOutcome: FC<{ fill: MdsFillResult }> = ({ fill }) => {
  const fields = fill.companyFieldsChanged ?? [];
  const created = fill.personsCreated ?? 0;
  const attached = fill.personsAttached ?? 0;
  const refreshedRows = fill.personsRefreshed ?? 0;
  const warnings = fill.warnings ?? [];

  const moved = [
    fields.length > 0 &&
      `${fields.length} company ${fields.length === 1 ? 'field' : 'fields'}`,
    created > 0 && `${created} ${created === 1 ? 'person' : 'people'} added`,
    attached > 0 && `${attached} attached to this company`,
    refreshedRows > 0 && `${refreshedRows} refreshed`,
    fill.rcsExtractLinked === true && 'the RCS extract',
  ].filter((entry): entry is string => typeof entry === 'string');

  return (
    <Flex vertical gap={8}>
      <Alert
        type={moved.length > 0 ? 'success' : 'info'}
        showIcon
        message={
          moved.length > 0 ? 'Filled from Master Data' : 'Nothing to bring over'
        }
        description={
          moved.length > 0 ? (
            <Flex vertical gap={4}>
              <span>{moved.join(', ')}.</span>
              {fields.length > 0 && (
                <Text type="secondary">Fields: {fields.join(', ')}.</Text>
              )}
            </Flex>
          ) : (
            'Legacy already matches what Master Data holds for this company.'
          )
        }
      />

      {/* Not folded into the panel above: these are the things a CSM has to go and fix in Master
          Data, and a company that is otherwise fine still needs to show them. */}
      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            warnings.length === 1
              ? 'One thing was not brought over'
              : `${warnings.length} things were not brought over`
          }
          description={
            <ul>
              {/* Index keys: these are display strings, not entities, and two people with the same
                  name failing the same read produce byte-identical warnings. */}
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}
    </Flex>
  );
};

/**
 * What actually happened, per person and per system.
 *
 * A provisioning that created the tenant but could not move Master Data is a success with a loose
 * end, not a failure: saying "failed" would invite a CSM to go looking for a company that already
 * exists. The loose end gets its own retry, which touches nothing in legacy.
 */
const ProvisionOutcome: FC<{
  result: ProvisionResult;
  onRetry: () => void;
  retrying: boolean;
}> = ({ result, onRetry, retrying }) => {
  const failed = result.members.filter((member) => member.outcome === 'failed');
  /**
   * People who are members but whom Keycloak does not know as clients yet.
   *
   * Scoped to members that actually have an account: a `failed` one carries `clientRoleGranted:
   * false` too, and reporting them here as well would name the same person in two panels for two
   * different reasons.
   *
   * `=== false`, not `!`: the field is hand-rolled over an unvalidated response, so a backend that
   * does not report it yet (or a rollback past it) sends `undefined` — and `!undefined` would tell
   * the CSM that nobody got the role after a run in which everybody did. Silence is the safe answer
   * to "the backend did not say".
   *
   * `outcome` is the whole test for "has an account", deliberately: `userId` is nullable in the type
   * but never null on a non-failed member (see `ProvisionedMember.userId`), and adding it here would
   * be a guard that cannot fire — or, if it ever did, one that hid a member without the role, which
   * is the single thing this panel exists to show.
   */
  const withoutRole = membersWithoutRole(result.members);

  return (
    <Flex vertical gap={8}>
      <Alert
        type={result.mdsUpdated ? 'success' : 'warning'}
        showIcon
        message={
          result.mdsUpdated
            ? `${result.company.name} is in service`
            : `${result.company.name} was provisioned, but Master Data still says incorporating`
        }
        description={
          <Flex vertical gap={8}>
            <span>
              Tenant <Text code>{result.company.schemaName}</Text>, BOB
              reference <Text code>{result.company.referenceId}</Text>.
              {!result.created &&
                ' The company already existed; this run finished the rest.'}
            </span>
            {!result.mdsUpdated && (
              <>
                <Text type="secondary">{result.mdsError}</Text>
                {/*
                 * Disabled while anybody is missing the role, and this is not tidiness: `runSync`
                 * closes the drawer on success, Master Data then says in service, and the company
                 * leaves a queue that lists only `incorporating` — so one click on the most
                 * prominent control here would discard the only in-app way to grant that role. The
                 * primary button does both halves in one run, which is why it is what the warning
                 * below points at.
                 *
                 * THE GUARD ONLY REACHES THIS MOUNT. Reopen the same still-`incorporating` row in a
                 * fresh drawer and `result` is null, so the standalone retry in the footer renders
                 * enabled with no idea that a role is missing — `details` reports membership but
                 * not role state, so nothing here can know. Closing that needs the endpoint to say
                 * so; until then this is a guard against the click that follows the run, not
                 * against every click.
                 */}
                <Tooltip
                  title={
                    withoutRole.length > 0
                      ? 'Use the primary button instead: it grants the missing easybiz-client role and moves Master Data in the same run. This one only moves Master Data, and closes the drawer.'
                      : ''
                  }
                >
                  {/*
                   * The span is what makes the tooltip reachable: a disabled button fires no mouse
                   * events, so a Tooltip wrapping one directly never opens — and this is the one
                   * tooltip that must, since it is the only place the reason for the disabling is
                   * written. The wrapper takes the hover instead.
                   */}
                  <span>
                    <Button
                      size="small"
                      onClick={onRetry}
                      loading={retrying}
                      disabled={withoutRole.length > 0}
                    >
                      Retry MDS status
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Flex>
        }
      />

      {withoutRole.length > 0 && (
        <Alert
          type="warning"
          showIcon
          // Out of the people who got an account, not out of everyone: a `failed` member is counted
          // by the panel below, and putting them in this denominator makes the run read as better
          // than it was ("2 of 3" when none of the three hold the role).
          message={`${withoutRole.length} of ${result.members.length - failed.length} people did not get the easybiz-client role`}
          description={
            <Flex vertical gap={8}>
              <span>
                They are members of the company, but Keycloak has not been told
                they are clients of the platform. Press{' '}
                <strong>the primary button below</strong> again — nothing is
                created twice.{' '}
                {result.mdsUpdated
                  ? // Master Data says in service, so the row is out of a queue that lists only
                    // `incorporating`: this drawer is the last way in. `Retry MDS status` is not
                    // named here on purpose — in this branch it is not rendered at all, and warning
                    // against a button that is not on screen sends the reader looking for it.
                    'Do it before closing this drawer: Master Data now says in service, so the company has left the queue and cannot be reopened from it.'
                  : // The MDS half did not land either, so the company is still incorporating — but
                    // do not promise it is on screen: once provisioned it drops out of the "Not
                    // provisioned" view, which is the one a CSM works from. Here the retry above
                    // does exist, and is disabled, so it is worth saying why.
                    'It moves Master Data in the same run, which is why "Retry MDS status" above is disabled. Master Data was not moved to in service, so the company is still incorporating — but it no longer appears under "Not provisioned".'}
                {/* No promise of a Slack alert: the backend posts it fire-and-forget and skips it
                    entirely where SLACK_ENABLED is off, so guaranteeing a notification here would
                    tell the CSM they can walk away from something that may leave no trace but a pod
                    log. What is guaranteed is the button above, while this drawer is open. */}
              </span>
              <RoleFailureList members={withoutRole} />
            </Flex>
          }
        />
      )}

      {failed.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${failed.length} of ${result.members.length} people did not get an account`}
          description={
            <S.ReasonList>
              {failed.map((member) => (
                <li key={member.personId}>
                  {member.email ?? member.keycloakId}: {member.error}
                </li>
              ))}
            </S.ReasonList>
          }
        />
      )}
    </Flex>
  );
};

export default IncorporationDrawer;
